import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCmsAuth } from "../../context/CmsAuthContext";
import { mergeCmsRoleCatalog } from "../../lib/cmsRoles";
import CmsUsersEditor from "./CmsUsersEditor";
import CmsRolesPanel from "./CmsRolesPanel";
import { ModuleHelpShell } from "../../components/help/ModuleHelpShell";
import { ModuleHelpToggle } from "../../components/help/ModuleHelpToggle";

const TABS = [
  { id: "usuarios", label: "Usuarios" },
  { id: "roles", label: "Roles", ownerOnly: true },
];

function allowedTab(id, isOwner) {
  return TABS.some((t) => t.id === id && (!t.ownerOnly || isOwner));
}

export default function CmsUsersPage() {
  const { profile, roleCatalog } = useCmsAuth();
  const isOwner = profile?.role === "owner";
  const [params] = useSearchParams();
  const [drafts, setDrafts] = useState(null);
  const catalog = useMemo(
    () => mergeCmsRoleCatalog(drafts || roleCatalog),
    [drafts, roleCatalog]
  );

  const raw = params.get("tab") || "usuarios";
  const tab = allowedTab(raw, isOwner) ? raw : "usuarios";
  const visibleTabs = TABS.filter((t) => !t.ownerOnly || isOwner);
  const unsavedRoleDraft = Boolean(drafts);

  return (
    <ModuleHelpShell moduleId="users">
    <div className="max-w-3xl space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
        <h2 className="font-display text-3xl font-black">Usuarios CMS</h2>
        <p className="text-sm text-brand-dark/55 mt-1">
          El rol define la plantilla. Owner y admin pueden autorizar módulos extra por usuario.
          El médico opera solo en SIGMO.
        </p>
        {unsavedRoleDraft ? (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 mt-3">
            Hay cambios de Roles sin guardar: ya se reflejan en cada usuario. Pulse Guardar roles
            para persistirlos.
          </p>
        ) : null}
        </div>
        <ModuleHelpToggle />
      </div>

      <nav className="flex flex-wrap gap-1 border-b border-brand-soft">
        {visibleTabs.map((t) => (
          <Link
            key={t.id}
            to={t.id === "usuarios" ? "/admin/users" : "/admin/users?tab=roles"}
            replace
            className={`px-4 py-2.5 text-sm ${
              tab === t.id
                ? "border-b-2 border-brand-primary font-bold text-brand-primary"
                : "text-brand-dark/55 hover:text-brand-dark"
            }`}
          >
            {t.label}
          </Link>
        ))}
      </nav>

      <div className={tab === "usuarios" ? "" : "hidden"}>
        <CmsUsersEditor catalog={catalog} embedded />
      </div>
      {isOwner ? (
        <div className={tab === "roles" ? "" : "hidden"}>
          <CmsRolesPanel
            catalog={catalog}
            sourceCatalog={roleCatalog}
            followSource={!drafts}
            onDraftsChange={setDrafts}
            onSaved={() => setDrafts(null)}
          />
        </div>
      ) : null}
    </div>
    </ModuleHelpShell>
  );
}
